import type { ClassDefinition, SetDefinition } from '../data/types';
import { seedRng } from '../shared/prng';
import { IDLE_INPUT } from './commands';
import type { ResolvedContent } from './content';
import { lookup } from './lookup';
import type { CoreState, PlayerId, PlayerState, SimState, SpeakerState, Vec2 } from './state';

export interface PlayerSlot {
  id: PlayerId;
  classId: string;
  name?: string;
}

const SPAWN_DIRECTIONS: readonly Vec2[] = [
  { x: -1, y: 0 },
  { x: 1, y: 0 },
  { x: 0, y: -1 },
  { x: 0, y: 1 },
];
const SPAWN_GAP_IN_PLAYER_RADII = 3;

export function createInitialState(
  seed: number,
  slots: readonly PlayerSlot[],
  content: ResolvedContent,
  set: SetDefinition,
  endless = false,
): SimState {
  if (slots.length === 0 || slots.length > SPAWN_DIRECTIONS.length) {
    throw new RangeError(
      `a game needs 1 to ${String(SPAWN_DIRECTIONS.length)} players, got ${String(slots.length)}`,
    );
  }
  const ids = new Set(slots.map((slot) => slot.id));
  if (ids.size !== slots.length) {
    throw new Error(`player ids must be unique, got ${slots.map((slot) => slot.id).join(', ')}`);
  }

  const core = createCore(set);

  return {
    seed,
    setId: set.id,
    tick: 0,
    status: 'running',
    rng: seedRng(seed),
    arena: { width: set.arena.width, height: set.arena.height },
    set: {
      tier: 0,
      segment: 'buildup',
      phrase: 0,
      bar: 0,
      beat: 0,
      segmentStartTick: 0,
      ...(endless ? { endlessFromTick: null } : {}),
    },
    core,
    players: slots.map((slot, index) =>
      createPlayer(slot, lookup(content.classes, slot.classId, 'class'), core, index, set),
    ),
    enemies: [],
    projectiles: [],
    traps: [],
    pickups: [],
    volume: 0,
    speakers: createSpeakers(set),
    placed: [],
    pendingUpgrades: [],
    nextEntityId: 1,
    stats: { kills: 0, phrasesHeld: 0, damageDealt: 0, vibesCollected: 0 },
    events: [],
  };
}

export function createCore(set: SetDefinition): CoreState {
  return {
    x: set.arena.width / 2,
    y: set.arena.height / 2,
    radius: set.core.radius,
    hp: set.core.maxHp,
    maxHp: set.core.maxHp,
  };
}

export function createSpeakers(set: SetDefinition): SpeakerState[] {
  return (set.speakers ?? []).map((speaker) => ({
    id: speaker.id,
    x: speaker.x,
    y: speaker.y,
    radius: speaker.radius,
    plugTicks: 0,
    plugged: false,
  }));
}

function createPlayer(
  slot: PlayerSlot,
  definition: ClassDefinition,
  core: CoreState,
  index: number,
  set: SetDefinition,
): PlayerState {
  const direction = SPAWN_DIRECTIONS[index] ?? { x: 0, y: 0 };
  const distance = core.radius + definition.radius * SPAWN_GAP_IN_PLAYER_RADII;
  const x = core.x + direction.x * distance;
  const y = core.y + direction.y * distance;
  return {
    id: slot.id,
    classId: definition.id,
    x,
    y,
    prevX: x,
    prevY: y,
    radius: definition.radius,
    hp: definition.maxHp,
    maxHp: definition.maxHp,
    speed: definition.speed,
    aim: IDLE_INPUT.aim,
    level: 1,
    vibes: 0,
    vibesToNextLevel: set.levelCurve.baseVibes,
    pendingLevelUps: 0,
    attackCooldown: 0,
    skillCooldown: 0,
    upgrades: [],
    modifiers: {},
    downed: false,
    invulnerableTicks: 0,
    weapons: [],
    ...(slot.name === undefined ? {} : { name: slot.name }),
    ...(definition.gauge === undefined ? {} : { gauge: definition.gauge.start ?? 0 }),
  };
}
